import React from 'react';
import { clsx } from 'clsx';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'beige' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  children: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  children,
  className,
  disabled,
  ...props
}) => {
  const baseClasses =
    'inline-flex items-center justify-center font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 active:scale-95 relative overflow-hidden group rounded-xl shadow-lg hover:shadow-xl';

  const variantClasses = {
    primary:
      'bg-gradient-to-r from-brand-blue to-brand-secondary-blue text-white hover:from-brand-secondary-blue hover:to-brand-blue focus:ring-brand-blue/50 shadow-brand-blue/25',
    secondary:
      'bg-gradient-to-r from-brand-beige-tint-1 to-brand-beige-tint-2 text-brand-blue hover:from-brand-blue hover:to-brand-secondary-blue hover:text-white focus:ring-brand-blue/50 border border-brand-blue/20',
    beige:
      'bg-gradient-to-r from-brand-beige to-brand-brown text-white hover:from-brand-blue hover:to-brand-secondary-blue focus:ring-brand-beige/50',
    outline:
      'border-2 border-brand-blue text-brand-blue hover:bg-gradient-to-r hover:from-brand-blue hover:to-brand-secondary-blue hover:text-white focus:ring-brand-blue/50 bg-transparent hover:border-transparent',
  };

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-sm',
    lg: 'px-6 py-4 text-base',
  };

  return (
    <button
      className={clsx(baseClasses, variantClasses[variant], sizeClasses[size], className)}
      disabled={disabled || loading}
      {...props}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transform -skew-x-12 transition-all duration-500 group-hover:translate-x-full"></div>

      <div className="relative z-10 flex items-center">
        {loading && (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
        )}
        {children}
      </div>
    </button>
  );
};
